/**
 * Local layer model and small pure rules shared by the store: default visibility, panel order,
 * z-order, metadata merge from the server, and validation of objects before they are applied.
 */
import {
  ANNOTATION_KINDS,
  MAX_DATA_BYTES,
  parseData,
  type AnnotationKind,
  type AnnotationObject,
  type DataByKind,
} from '../../../shared/annotations';
import { SYNC_LIMITS, type LayerDTO, type LayerShareTarget } from '../../../shared/dto/annotations';
import { utf8Length, type OpBody } from './queue';

/** A layer as stored on this device (Dexie `layers`). */
export interface LayerRecord {
  id: string;
  sheetFileId: string;
  ownerId: string;
  ownerName: string;
  ownerIsEditor: boolean;
  ownerInactive: boolean;
  name: string;
  sheetRev: number;
  updatedAt: number;
  maxSeq: number;
  shares: LayerShareTarget[];
  /** When this device first saw the layer (creation order of own layers). */
  firstSeen: number;
}

export const MAX_LAYER_NAME = SYNC_LIMITS.layerName;

/** Trims, collapses whitespace and caps a layer name; empty → fallback. */
export function cleanLayerName(name: string, fallback: string): string {
  const n = (name ?? '').replace(/\s+/g, ' ').trim();
  return (n || fallback).slice(0, MAX_LAYER_NAME);
}

/** Own layers and layers shared by editors (conductor, assistants) are shown unless hidden. */
export function defaultVisible(layer: Pick<LayerRecord, 'ownerId' | 'ownerIsEditor'>, userId: string): boolean {
  return layer.ownerId === userId || layer.ownerIsEditor;
}

const collator = typeof Intl !== 'undefined' ? new Intl.Collator('is', { sensitivity: 'base', numeric: true }) : null;
const cmpText = (a: string, b: string) => (collator ? collator.compare(a, b) : a < b ? -1 : a > b ? 1 : 0);

/** Panel order: own layers in creation order, then shared layers (editors' first, then by owner and name). */
export function compareLayers(a: LayerRecord, b: LayerRecord, userId: string): number {
  const am = a.ownerId === userId;
  const bm = b.ownerId === userId;
  if (am !== bm) return am ? -1 : 1;
  if (am) return a.firstSeen - b.firstSeen || cmpText(a.name, b.name) || (a.id < b.id ? -1 : 1);
  if (a.ownerIsEditor !== b.ownerIsEditor) return a.ownerIsEditor ? -1 : 1;
  return cmpText(a.ownerName, b.ownerName) || cmpText(a.name, b.name) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

export function sortLayers(layers: Iterable<LayerRecord>, userId: string): LayerRecord[] {
  return [...layers].sort((a, b) => compareLayers(a, b, userId));
}

/**
 * Z-order (bottom → top) for drawing: shared layers below own layers, each group in panel order.
 * Returns layerId → z for the given visible layers.
 */
export function zOrder(sortedVisible: readonly LayerRecord[], userId: string): Map<string, number> {
  const z = new Map<string, number>();
  let i = 0;
  for (const l of sortedVisible) if (l.ownerId !== userId) z.set(l.id, i++);
  for (const l of sortedVisible) if (l.ownerId === userId) z.set(l.id, i++);
  return z;
}

/** Drawn on an older revision of the sheet than `fileRev` (the "gert á eldri útgáfu" state). */
export function isStale(layer: Pick<LayerRecord, 'sheetRev'>, fileRev: number): boolean {
  return layer.sheetRev < fileRev;
}

/** What a queued layer op of this device keeps over the server copy (see layerFromDTO). */
export interface KeepLocal {
  /** A create or rename is queued: the local name is newer. */
  name: boolean;
  /** Any layer op is queued (incl. a revision confirmation): keep the newer sheet revision. */
  rev: boolean;
}

/** What the queued layer op (if any) of a layer keeps over a server copy that arrives meanwhile. */
export function keepLocal(queued: OpBody | undefined): KeepLocal {
  if (!queued || queued.type === 'ann') return { name: false, rev: false };
  // A pure revision confirmation (keepName) carries no name change: the server's name wins.
  return { name: !(queued.type === 'layer.upsert' && queued.payload.keepName), rev: true };
}

/**
 * Server metadata → local record. While a layer op of this device is still queued the server copy
 * is older: `keep.name` keeps the local name (create/rename queued) and `keep.rev` the newer of the
 * two sheet revisions (sheetRev only ever moves forward on the server). `true` = both.
 */
export function layerFromDTO(dto: LayerDTO, existing: LayerRecord | undefined, now: number, keep: boolean | KeepLocal): LayerRecord {
  const keepName = typeof keep === 'boolean' ? keep : keep.name;
  const keepRev = typeof keep === 'boolean' ? keep : keep.rev;
  return {
    id: dto.id,
    sheetFileId: dto.sheetFileId,
    ownerId: dto.ownerId,
    ownerName: dto.ownerName,
    ownerIsEditor: !!dto.ownerIsEditor,
    ownerInactive: !!dto.ownerInactive,
    name: keepName && existing ? existing.name : dto.name,
    sheetRev: keepRev && existing ? Math.max(existing.sheetRev, dto.sheetRev) : dto.sheetRev,
    updatedAt: dto.updatedAt,
    maxSeq: dto.maxSeq,
    shares: Array.isArray(dto.shares) ? dto.shares : (existing?.shares ?? []),
    firstSeen: existing?.firstSeen ?? now,
  };
}

/** Shallow equality of the fields a LayerDTO can change (avoids needless writes/re-renders). */
export function sameLayer(a: LayerRecord, b: LayerRecord): boolean {
  return (
    a.ownerName === b.ownerName &&
    a.ownerIsEditor === b.ownerIsEditor &&
    a.ownerInactive === b.ownerInactive &&
    a.name === b.name &&
    a.sheetRev === b.sheetRev &&
    a.updatedAt === b.updatedAt &&
    a.maxSeq === b.maxSeq &&
    a.sheetFileId === b.sheetFileId &&
    JSON.stringify(a.shares) === JSON.stringify(b.shares)
  );
}

/** Highest page index an object may sit on (connector targets are limited to 0..499 as well). */
export const MAX_PAGE_INDEX = SYNC_LIMITS.maxPage;

export function isValidPage(page: unknown, pageCount: number | null): page is number {
  if (typeof page !== 'number' || !Number.isInteger(page) || page < 0 || page > MAX_PAGE_INDEX) return false;
  return pageCount === null || pageCount <= 0 || page < pageCount;
}

export function isKind(k: unknown): k is AnnotationKind {
  return typeof k === 'string' && (ANNOTATION_KINDS as readonly string[]).includes(k);
}

/** Validates a payload for a kind and checks the 32 KB row limit. Null = drop it. */
export function validData<K extends AnnotationKind>(kind: K, data: unknown): DataByKind[K] | null {
  const parsed = parseData(kind, data);
  if (!parsed) return null;
  if (utf8Length(JSON.stringify(parsed)) > MAX_DATA_BYTES) return null;
  return parsed;
}

/** Tombstone for a live object (payload dropped, like on the server). */
export function tombstone(o: AnnotationObject, hlcMs: number, deviceId: string, at: number): AnnotationObject {
  return { ...o, data: {} as never, hlcMs, deviceId, deletedAt: at } as AnnotationObject;
}
